'use client';

import { companyCountryCode } from '@/lib/countries';
import { memo, useCallback, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
  CreditsUsedPopup,
  creditsUsedSnapshot,
  type CreditsUsedSnapshot,
} from '@/new_components/shared/CreditsUsedPopup';
import { InsufficientCreditsDialog } from '@/new_components/shared/InsufficientCreditsDialog';
import {
  ChevronDown,
  Download,
  Loader2,
  RefreshCw,
  Wallet,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { WEB_CREDIT_COSTS } from '@/config/credit-costs.config';
import { trackFileDownload } from '@/lib/analytics';
import type {
  RegisterDocumentItem,
  RegisterFileBasicItem,
} from '@/lib/backend-api';
import { trackSignupAndRedirect } from '@/lib/track-signup-click';
import { useApplyCreditBalance } from '@/hooks/use-credit-balance';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LocaleLink } from '@/components/locale-link';
import {
  confirmedAbsentRegisterDocTypes,
  constitutionalDocKind,
  expectedRegisterDocTypes,
  formatCapturedDate,
  GESELLSCHAFTER_AUTO_UPDATED_TYPES,
  LOCALE_MAP,
  REFRESHABLE_FILE_TYPES,
  REGISTER_AUTO_UPDATED_TYPES,
} from './companyDetailUtils';
import { SourceAttribution } from './SourceAttribution';

const SOFT_ERROR_CODES = new Set([
  'file_not_available',
  'file_not_found',
  'company_not_available',
  'insufficient_credits',
  'document_not_found',
]);

/**
 * Right-column card listing Handelsregister printouts and filed documents.
 *
 * The backend auto-refreshes CD (via `/register`) and DK (via `/gesellschafter`), so while
 * those endpoints are in flight the corresponding "Fetch" button renders a spinner instead
 * of being clickable. Manual refresh is allowed for AD and falls back if the auto-refresh
 * path is idle. Synchronously opens a blank tab on download to avoid popup blockers.
 *
 * Guests get a disabled-looking "Sign in" button that tracks the prompt and redirects.
 * When `isDeleted` is true, only downloads of existing files are allowed — refresh is hidden.
 */
export const RegisterDocsCard = memo(function RegisterDocsCard({
  registerFiles,
  registerDocuments = [],
  registerDocumentsComplete = [],
  dkRegisterFile,
  hasGesellschafterliste,
  rechtsformCode,
  gruendungsdokument,
  ersteEintragungDatum,
  euId,
  isAuthenticated,
  isRegisterLoading = false,
  isGesellschafterLoading = false,
  isDeleted = false,
  showRegisterAttribution = false,
  registerCourt,
  registerId,
  capturedAt,
  className,
  id,
}: {
  registerFiles: RegisterFileBasicItem[];
  registerDocuments?: RegisterDocumentItem[];
  registerDocumentsComplete?: string[];
  /** DK file info from the /gesellschafter endpoint (separate from register_files). */
  dkRegisterFile: RegisterFileBasicItem | null;
  hasGesellschafterliste: boolean;
  rechtsformCode?: number | null;
  gruendungsdokument?: string | null;
  ersteEintragungDatum?: string | null;
  euId: string;
  isAuthenticated: boolean;
  /** When true, /register is still fetching — CD update button shows a spinner. */
  isRegisterLoading?: boolean;
  /** When true, /gesellschafter is still fetching — DK update button shows a spinner. */
  isGesellschafterLoading?: boolean;
  /** When true, the company is deleted — hide refresh/update buttons, allow only downloads. */
  isDeleted?: boolean;
  showRegisterAttribution?: boolean;
  registerCourt?: string | null;
  registerId?: string | null;
  capturedAt?: string | null;
  /** Layout classes from the page (mobile `order-*`, borders) — see MOBILE_ORDER. */
  className?: string;
  /** Scroll anchor for the mobile action bar. */
  id?: string;
}) {
  const { t, i18n } = useTranslation();
  const pathname = usePathname();
  const locale = LOCALE_MAP[i18n.language] || 'en-US';
  const [downloadingFileTypes, setDownloadingFileTypes] = useState<Set<string>>(
    new Set(),
  );
  const [expandedFileTypes, setExpandedFileTypes] = useState<Set<string>>(
    new Set(),
  );
  const [storedDocumentKeys, setStoredDocumentKeys] = useState<Set<string>>(
    new Set(),
  );

  const formatDate = useCallback(
    (dateStr: string | null | undefined) => {
      if (!dateStr) return null;
      try {
        return new Date(dateStr).toLocaleDateString(locale, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
      } catch {
        return null;
      }
    },
    [locale],
  );

  const filteredFiles = useMemo(() => {
    const REGISTER_SORT_ORDER: Record<string, number> = {
      HandelsRegisterAD: 0,
      HandelsRegisterCD: 1,
      HandelsRegisterDKGesellschafter: 2,
      HandelsRegisterDKSatzung: 3,
      HandelsRegisterDKMusterProtokol: 4,
      HandelsRegisterDKAnmeldung: 5,
    };
    const expectedTypes = expectedRegisterDocTypes({
      country: companyCountryCode(null, euId),
      hasGesellschafterliste,
      rechtsformCode,
      gruendungsdokument,
      ersteEintragungDatum,
    });

    // Merge register files with DK file from gesellschafter response.
    const allFiles = [...registerFiles];
    if (dkRegisterFile) allFiles.push(dkRegisterFile);

    const byType = new Map<string, RegisterFileBasicItem>();
    for (const f of allFiles) {
      if (!REFRESHABLE_FILE_TYPES.has(f.file_type)) continue;
      const existing = byType.get(f.file_type);
      if (existing) {
        const existingDate = existing.published_at ?? existing.fetched_at ?? '';
        const fDate = f.published_at ?? f.fetched_at ?? '';
        if (fDate <= existingDate) continue;
      }
      byType.set(f.file_type, f);
    }
    const confirmedAbsentTypes = confirmedAbsentRegisterDocTypes(
      registerDocumentsComplete,
      new Set(byType.keys()),
      registerDocuments,
    );
    // Trust absence only after a complete catalog walk, with no stored file or catalog entry.
    const visibleExpectedTypes = expectedTypes.filter(
      (type) => !confirmedAbsentTypes.has(type),
    );
    return [...new Set([...visibleExpectedTypes, ...byType.keys()])]
      .map(
        (ft) => byType.get(ft) ?? ({ file_type: ft } as RegisterFileBasicItem),
      )
      .sort(
        (a, b) =>
          (REGISTER_SORT_ORDER[a.file_type] ?? 9) -
          (REGISTER_SORT_ORDER[b.file_type] ?? 9),
      );
  }, [
    euId,
    registerFiles,
    registerDocuments,
    registerDocumentsComplete,
    dkRegisterFile,
    hasGesellschafterliste,
    rechtsformCode,
    gruendungsdokument,
    ersteEintragungDatum,
  ]);

  // Track locally updated fetched_at after a successful refresh
  const [refreshedDates, setRefreshedDates] = useState<Record<string, string>>(
    {},
  );
  const onFileRefreshed = useCallback((fileType: string, fetchedAt: string) => {
    setRefreshedDates((prev) => ({ ...prev, [fileType]: fetchedAt }));
  }, []);

  const [failedFileTypes, setFailedFileTypes] = useState<Set<string>>(
    new Set(),
  );
  /** File types flagged as "soft" errors — don't show retry, show explanation. */
  const [unavailableFileTypes, setUnavailableFileTypes] = useState<Set<string>>(
    new Set(),
  );
  /** Last error per file type: backend code + optional backend-supplied message. */
  const [fileErrorDetails, setFileErrorDetails] = useState<
    Record<string, { code: string; message?: string }>
  >({});
  const [loadingFileTypes, setLoadingFileTypes] = useState<Set<string>>(
    new Set(),
  );
  // Centered "out of credits" modal linking to the pricing page — shown once
  // per failed attempt on top of the inline amber hint in the card header.
  const [creditsDialogOpen, setCreditsDialogOpen] = useState(false);
  // Centered "N credits used, M left" confirmation after a successful charge.
  const [creditsUsed, setCreditsUsed] = useState<CreditsUsedSnapshot | null>(
    null,
  );
  const applyCreditBalance = useApplyCreditBalance();

  /**
   * Open a register file.
   * - Existing file (fetched_at today): backend presigns the existing file.
   * - Abrufen (refresh): backend scrapes the file first, then presigns.
   */
  const openRegisterFile = useCallback(
    async (
      fileType: string,
      refresh: boolean,
      documentId?: number,
      docDate?: string | null,
    ) => {
      if (!isAuthenticated) return;
      const fileKey =
        documentId === undefined ? fileType : `${fileType}:${documentId}`;
      if (refresh) {
        if (loadingFileTypes.has(fileKey)) return;
        setLoadingFileTypes((prev) => new Set(prev).add(fileKey));
      } else {
        if (downloadingFileTypes.has(fileKey)) return;
        setDownloadingFileTypes((prev) => new Set(prev).add(fileKey));
      }
      setFailedFileTypes((prev) => {
        const next = new Set(prev);
        next.delete(fileKey);
        return next;
      });
      setUnavailableFileTypes((prev) => {
        const next = new Set(prev);
        next.delete(fileKey);
        return next;
      });
      setFileErrorDetails((prev) => {
        if (!(fileKey in prev)) return prev;
        const next = { ...prev };
        delete next[fileKey];
        return next;
      });
      // Success is logged server-side (register_document_*download, with the
      // document id); only failures are logged from here.
      const event = refresh ? 'document_refresh' : 'document_download';
      // For downloads, open a tab synchronously (avoids popup blockers) with a loading message
      const newTab = refresh ? null : window.open('about:blank', '_blank');
      if (newTab) {
        newTab.document.title = 'Laden...';
        newTab.document.body.innerHTML =
          '<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui;color:#666"><p>Dokument wird geladen...</p></div>';
      }
      try {
        const qs = new URLSearchParams({ file_type: fileType });
        if (refresh) qs.set('refresh', 'true');
        if (documentId !== undefined) qs.set('document_id', String(documentId));
        const response = await fetch(
          `/api/companies/${encodeURIComponent(euId)}/register-file/download?${qs}`,
        );
        // Parse manually so a non-JSON body (proxy/gateway timeout HTML, empty
        // 5xx, etc.) yields a descriptive error instead of Safari's opaque
        // "The string did not match the expected pattern".
        const rawText = await response.text();
        let result: {
          success?: boolean;
          data: {
            url: string;
            fetched_at?: string;
            credits_charged?: number | null;
            credits_balance?: number | null;
            entitled?: boolean;
          };
          code?: string;
          message?: string;
        };
        try {
          result = JSON.parse(rawText);
        } catch {
          throw new Error(
            `Non-JSON response (${response.status}): ${rawText.slice(0, 300)}`,
          );
        }
        if (!response.ok || !result.success) {
          newTab?.close();
          const code: string =
            typeof result?.code === 'string' ? result.code : 'download_failed';
          const backendMessage: string | undefined =
            typeof result?.message === 'string' ? result.message : undefined;
          setFileErrorDetails((prev) => ({
            ...prev,
            [fileKey]: { code, message: backendMessage },
          }));
          if (SOFT_ERROR_CODES.has(code)) {
            setUnavailableFileTypes((prev) => new Set(prev).add(fileKey));
          } else {
            setFailedFileTypes((prev) => new Set(prev).add(fileKey));
          }
          if (code === 'insufficient_credits') {
            setCreditsDialogOpen(true);
          }
          fetch('/api/log/page-view', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              event: `${event}_failed`,
              entityId: euId,
              entityType: 'file',
              meta: {
                fileType,
                documentId: documentId ?? null,
                docDate: docDate?.slice(0, 10) ?? null,
                refresh,
              },
              description: `Failed: ${fileType} (${response.status} ${code}) body=${rawText.slice(0, 500)}`,
              url: window.location.pathname,
            }),
            keepalive: true,
          }).catch(() => {});
          return;
        }
        trackFileDownload(euId, fileType, 0);
        setCreditsUsed(
          creditsUsedSnapshot(
            result.data.credits_charged,
            result.data.credits_balance,
            // A refresh always bills, so `entitled` only ever means "you
            // already own this copy" on a plain view.
            { entitled: result.data.entitled === true, document: true },
          ),
        );
        applyCreditBalance(result.data.credits_balance);
        if (refresh) {
          if (documentId !== undefined) {
            setStoredDocumentKeys((prev) => new Set(prev).add(fileKey));
          } else {
            // Update the fetched_at of this file type locally from the backend response
            onFileRefreshed(
              fileType,
              result.data.fetched_at || new Date().toISOString(),
            );
          }
        } else if (newTab) {
          const fileUrl: string = result.data.url;
          const isTiff = /\.tiff?(\?|$)/i.test(fileUrl);
          if (isTiff) {
            // TIFF files can't be rendered inline by browsers — download directly
            // using a temporary link instead of leaving about:blank open
            newTab.close();
            const a = document.createElement('a');
            a.href = fileUrl;
            a.download = '';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          } else {
            newTab.location.href = fileUrl;
          }
        }
      } catch (err) {
        newTab?.close();
        setFailedFileTypes((prev) => new Set(prev).add(fileKey));
        if (documentId !== undefined) {
          setFileErrorDetails((prev) => ({
            ...prev,
            [fileKey]: { code: 'download_failed' },
          }));
        }
        fetch('/api/log/page-view', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: `${event}_failed`,
            entityId: euId,
            entityType: 'file',
            meta: {
              fileType,
              documentId: documentId ?? null,
              docDate: docDate?.slice(0, 10) ?? null,
              refresh,
            },
            description: `Error ${fileType}: ${err instanceof Error ? err.message : 'unknown'}`,
            url: window.location.pathname,
          }),
          keepalive: true,
        }).catch(() => {});
      } finally {
        if (refresh)
          setLoadingFileTypes((prev) => {
            const next = new Set(prev);
            next.delete(fileKey);
            return next;
          });
        else
          setDownloadingFileTypes((prev) => {
            const next = new Set(prev);
            next.delete(fileKey);
            return next;
          });
      }
    },
    [
      euId,
      isAuthenticated,
      loadingFileTypes,
      downloadingFileTypes,
      onFileRefreshed,
      applyCreditBalance,
    ],
  );

  const getFileName = (fileType: string, publicName?: string) => {
    switch (fileType) {
      case 'HandelsRegisterAD':
        return t('companyDetail.registerDocs.adName');
      case 'HandelsRegisterCD':
        return t('companyDetail.registerDocs.cdName');
      case 'HandelsRegisterDKGesellschafter':
        return t('companyDetail.registerDocs.dkName');
      case 'HandelsRegisterDKSatzung':
        return t(
          `companyDetail.registerDocs.constitutionalName.${constitutionalDocKind(gruendungsdokument, rechtsformCode)}`,
        );
      case 'HandelsRegisterDKMusterProtokol':
        return t('companyDetail.registerDocs.musterprotokollName');
      case 'HandelsRegisterDKAnmeldung':
        return t('companyDetail.registerDocs.anmeldungName');
      default:
        return publicName || fileType;
    }
  };

  return (
    <Card variant="grid" className={className} id={id}>
      <CardHeader>
        <CardTitle as="h2">
          {t('companyDetail.registerFiles', 'Register Files')}
        </CardTitle>
        {!isAuthenticated && (
          <p className="text-xs text-muted-foreground">
            {t('companyDetail.registerDocs.guestHint')}
          </p>
        )}
        {Object.values(fileErrorDetails).some(
          (detail) => detail.code === 'insufficient_credits',
        ) && (
          <div
            role="alert"
            className="mt-1 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200"
          >
            <Wallet className="size-3.5 shrink-0 mt-0.5" aria-hidden />
            <div className="flex flex-col gap-1">
              <span className="font-medium">
                {t('companyDetail.registerDocs.errors.insufficient_credits')}
              </span>
              <LocaleLink
                href="/plans"
                className="inline-flex w-fit items-center gap-1 font-semibold underline underline-offset-2 hover:opacity-80"
              >
                {t(
                  'companyDetail.registerDocs.errors.insufficient_credits_cta',
                )}{' '}
                →
              </LocaleLink>
            </div>
          </div>
        )}
        {Object.entries(fileErrorDetails)
          .filter(
            ([key, detail]) =>
              !key.includes(':') &&
              SOFT_ERROR_CODES.has(detail.code) &&
              detail.code !== 'insufficient_credits',
          )
          .map(([ft, detail]) => {
            const name = getFileName(ft);
            const translated = t(
              `companyDetail.registerDocs.errors.${detail.code}`,
              {
                name,
                defaultValue: '',
              },
            );
            const text =
              translated ||
              detail.message ||
              t('companyDetail.registerDocs.errors.generic', { name });
            return (
              <p
                key={ft}
                className="text-xs text-amber-600 dark:text-amber-400"
              >
                {text}
              </p>
            );
          })}
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        <div className="flex flex-col divide-y">
          {filteredFiles.map((file) => {
            const effectiveFetchedAt =
              refreshedDates[file.file_type] || file.fetched_at;
            const dateStr = effectiveFetchedAt
              ? formatDate(effectiveFetchedAt)
              : null;
            const canDownload = !!effectiveFetchedAt;
            // CD is auto-updated by /register; DK is auto-updated by /gesellschafter
            const isAutoUpdating =
              (isRegisterLoading &&
                REGISTER_AUTO_UPDATED_TYPES.has(file.file_type)) ||
              (isGesellschafterLoading &&
                GESELLSCHAFTER_AUTO_UPDATED_TYPES.has(file.file_type));
            // The update/refresh button is busy either from manual refresh or auto-update
            const isRefreshing =
              loadingFileTypes.has(file.file_type) || isAutoUpdating;
            const documents = registerDocuments.filter(
              (item) => item.file_type === file.file_type,
            );
            const matchedIndex = canDownload
              ? documents.findIndex(
                  (item) =>
                    item.doc_date !== null &&
                    (item.doc_date === file.doc_date?.slice(0, 10) ||
                      item.doc_date === file.published_at?.slice(0, 10)),
                )
              : -1;
            // Select the newest document when no stored file matches.
            const currentIndex =
              matchedIndex >= 0
                ? matchedIndex
                : documents.reduce(
                    (newestIndex, item, index) =>
                      (item.doc_date ?? '') >
                      (documents[newestIndex]?.doc_date ?? '')
                        ? index
                        : newestIndex,
                    0,
                  );
            const publishedAt = canDownload
              ? file.published_at ?? documents[currentIndex]?.doc_date
              : documents[currentIndex]?.doc_date ?? file.published_at;
            const earlierDocuments = documents.filter(
              (_, index) => index !== currentIndex,
            );

            return (
              <div key={file.file_type}>
                <div className="flex items-center gap-2 sm:gap-3 py-1 sm:py-3">
                  <img
                    src="/media/file-types/pdf.svg"
                    alt={`${getFileName(file.file_type, file.public_name)} (PDF)`}
                    className="h-4 w-4 sm:h-5 sm:w-5 shrink-0"
                  />
                  <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                    <span className="text-[10px] sm:text-sm font-medium">
                      {getFileName(file.file_type, file.public_name)}
                    </span>
                    {/* DK publication date as a <time> element so crawlers get a
                      machine-readable date (as in the Jahresabschlüsse card). */}
                    {file.file_type.startsWith('HandelsRegisterDK') &&
                      publishedAt && (
                        <span className="text-[9px] sm:text-xs leading-tight text-muted-foreground truncate">
                          {t('companyDetail.publishedAt', 'Published on')}{' '}
                          <time dateTime={publishedAt.slice(0, 10)}>
                            {formatCapturedDate(
                              publishedAt,
                              i18n.language,
                            ) ?? formatDate(publishedAt)}
                          </time>
                        </span>
                      )}
                    {isAuthenticated && dateStr && (
                      <span className="text-[10px] leading-tight text-gray-400 inline-flex items-center gap-1">
                        {t('companyDetail.updatedAt', 'Updated at')}: {dateStr}
                        {!isDeleted &&
                          !isRefreshing &&
                          !unavailableFileTypes.has(file.file_type) && (
                            <button
                              type="button"
                              onClick={() =>
                                openRegisterFile(
                                  file.file_type,
                                  true,
                                  undefined,
                                  file.doc_date ?? file.published_at,
                                )
                              }
                              className="inline-flex items-center justify-center size-3.5 rounded-full hover:bg-muted transition-colors"
                              title={t(
                                'companyDetail.registerDocs.fetch',
                                'Fetch',
                              )}
                            >
                              <RefreshCw className="size-2" />
                            </button>
                          )}
                        {!isDeleted && isRefreshing && (
                          <Loader2 className="size-2 animate-spin" />
                        )}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col items-stretch gap-1 shrink-0 w-[110px]">
                    {/* Anzeigen button: shown when file exists, stays clickable
                      even during refresh and after a failed refresh.
                      Always downloads the cached file; retries happen via the
                      inline refresh icon next to the date. */}
                    {isAuthenticated && canDownload && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="min-h-[44px] sm:min-h-0 -my-2 sm:my-0 h-auto sm:h-7 text-[10px] sm:text-sm lg:text-xs px-2 sm:px-3 border-0 lg:border bg-transparent lg:bg-background rounded-none shadow-none"
                              onClick={() =>
                                openRegisterFile(
                                  file.file_type,
                                  false,
                                  undefined,
                                  file.doc_date ?? file.published_at,
                                )
                              }
                            >
                              <Download className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                              {t('companyDetail.registerDocs.view', 'View')}
                            </Button>
                          </TooltipTrigger>
                          {/* Price on hover/focus, before the click. */}
                          <TooltipContent>
                            {t('credits.costTooltip', {
                              n: WEB_CREDIT_COSTS.registerFileDownload,
                              defaultValue:
                                'Costs {{n}} credits — once you have paid for a document it stays free.',
                            })}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {/* Abrufen button when no file exists yet — clickable
                      in the idle state, including after a failed refresh so
                      the user can retry. */}
                    {isAuthenticated &&
                      !isDeleted &&
                      !loadingFileTypes.has(file.file_type) &&
                      !downloadingFileTypes.has(file.file_type) &&
                      !canDownload &&
                      !unavailableFileTypes.has(file.file_type) &&
                      !isAutoUpdating && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="min-h-[44px] sm:min-h-0 -my-2 sm:my-0 h-auto sm:h-7 text-[10px] sm:text-sm lg:text-xs px-2 sm:px-3 border-0 lg:border bg-transparent lg:bg-background rounded-none shadow-none"
                                onClick={() =>
                                  openRegisterFile(
                                    file.file_type,
                                    true,
                                    undefined,
                                    file.doc_date ?? file.published_at,
                                  )
                                }
                              >
                                <RefreshCw className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                                {t(
                                  'companyDetail.registerDocs.fetch',
                                  'Fetch',
                                )}
                              </Button>
                            </TooltipTrigger>
                            {/* Refresh is a live registry scrape — always
                              billed, so no "stays free" clause here. */}
                            <TooltipContent>
                              {t('credits.costTooltipRefresh', {
                                n: WEB_CREDIT_COSTS.registerFileDownload,
                                defaultValue:
                                  'Costs {{n}} credits — a manual update fetches a fresh copy.',
                              })}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    {/* Abrufen disabled with spinner during refresh
                      (manual click or auto-update) so the slot stays filled. */}
                    {isAuthenticated &&
                      !isDeleted &&
                      !downloadingFileTypes.has(file.file_type) &&
                      !canDownload &&
                      (loadingFileTypes.has(file.file_type) ||
                        isAutoUpdating) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-h-[44px] sm:min-h-0 -my-2 sm:my-0 h-auto sm:h-7 text-[10px] sm:text-sm lg:text-xs px-2 sm:px-3 border-0 lg:border bg-transparent lg:bg-background rounded-none shadow-none"
                          disabled
                        >
                          <Loader2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 animate-spin mr-1" />
                          {t('companyDetail.registerDocs.fetch', 'Fetch')}
                        </Button>
                      )}
                    {!isAuthenticated && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="min-h-[44px] sm:min-h-0 -my-2 sm:my-0 h-auto sm:h-7 text-[10px] sm:text-sm lg:text-xs px-2 sm:px-3 whitespace-nowrap border-0 lg:border bg-transparent lg:bg-background rounded-none shadow-none"
                              onClick={() =>
                                trackSignupAndRedirect(
                                  pathname,
                                  'register_docs',
                                  euId,
                                )
                              }
                            >
                              <Download className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                              {t(
                                'companyDetail.registerDocs.signup',
                                'Sign in',
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {t('companyDetail.registerDocs.loginToDownload')}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
                {earlierDocuments.length > 0 && (
                  <div className="pb-2 pl-6 lg:pl-8">
                    <button
                      type="button"
                      aria-expanded={expandedFileTypes.has(file.file_type)}
                      onClick={() =>
                        setExpandedFileTypes((prev) => {
                          const next = new Set(prev);
                          if (next.has(file.file_type))
                            next.delete(file.file_type);
                          else next.add(file.file_type);
                          return next;
                        })
                      }
                      className="inline-flex min-h-[40px] items-center gap-1 text-[10px] lg:text-xs text-muted-foreground hover:text-foreground"
                    >
                      {t('companyDetail.registerDocs.earlierVersions', {
                        count: earlierDocuments.length,
                      })}
                      <ChevronDown
                        className={`size-3 transition-transform ${expandedFileTypes.has(file.file_type) ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {expandedFileTypes.has(file.file_type) && (
                      <div className="flex flex-col">
                        {earlierDocuments.map((item) => {
                          const itemKey = `${item.file_type}:${item.id}`;
                          const stored =
                            item.stored || storedDocumentKeys.has(itemKey);
                          const loading =
                            loadingFileTypes.has(itemKey) ||
                            (!stored && isAutoUpdating);
                          const downloading = downloadingFileTypes.has(itemKey);
                          const date =
                            item.doc_date ?? item.published_at?.slice(0, 10);
                          const dateLabel = date
                            ? t('companyDetail.registerDocs.versionOf', {
                                date: '__DATE__',
                              }).split('__DATE__')
                            : null;
                          const detail = fileErrorDetails[itemKey];
                          const translatedError = detail
                            ? t(
                                `companyDetail.registerDocs.errors.${detail.code}`,
                                {
                                  name: getFileName(item.file_type),
                                  defaultValue: '',
                                },
                              )
                            : '';
                          return (
                            <div key={item.id} className="py-1">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <div className="min-w-0 flex-1 text-[10px] lg:text-xs text-muted-foreground">
                                  {date && dateLabel ? (
                                    <span>
                                      {dateLabel[0]}
                                      <time dateTime={item.doc_date ?? date}>
                                        {formatCapturedDate(
                                          date,
                                          i18n.language,
                                        ) ?? formatDate(date)}
                                      </time>
                                      {dateLabel[1]}
                                    </span>
                                  ) : (
                                    getFileName(item.file_type)
                                  )}
                                </div>
                                <div className="w-[110px] shrink-0">
                                  {isAuthenticated &&
                                    (stored || !isDeleted) && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="min-h-[44px] sm:min-h-0 -my-2 sm:my-0 h-auto sm:h-7 text-[10px] sm:text-sm lg:text-xs px-2 sm:px-3 border-0 lg:border bg-transparent lg:bg-background rounded-none shadow-none w-full"
                                              disabled={loading || downloading}
                                              onClick={() =>
                                                openRegisterFile(
                                                  item.file_type,
                                                  !stored,
                                                  item.id,
                                                  date,
                                                )
                                              }
                                            >
                                              {loading || downloading ? (
                                                <Loader2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 animate-spin mr-1" />
                                              ) : stored ? (
                                                <Download className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                                              ) : (
                                                <RefreshCw className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                                              )}
                                              {t(
                                                stored
                                                  ? 'companyDetail.registerDocs.view'
                                                  : 'companyDetail.registerDocs.fetch',
                                              )}
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            {t(
                                              stored
                                                ? 'credits.costTooltip'
                                                : 'credits.costTooltipRefresh',
                                              {
                                                n: WEB_CREDIT_COSTS.registerFileDownload,
                                              },
                                            )}
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                </div>
                              </div>
                              {detail && (
                                <p
                                  className="text-[10px] text-amber-600 dark:text-amber-400"
                                  role="alert"
                                >
                                  {translatedError ||
                                    detail.message ||
                                    t(
                                      'companyDetail.registerDocs.errors.generic',
                                      { name: getFileName(item.file_type) },
                                    )}
                                </p>
                              )}
                            </div>
                          );
                        })}
                        {!isAuthenticated && (
                          <p className="text-[10px] text-muted-foreground">
                            {t('companyDetail.registerDocs.signup')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {(loadingFileTypes.size > 0 || isRegisterLoading) && (
          <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1.5">
            <Loader2 className="size-2.5 animate-spin shrink-0" />
            {t(
              'companyDetail.registerDocs.refreshHint',
              'Refreshing may take up to 2 minutes.',
            )}
          </p>
        )}
        {loadingFileTypes.size === 0 &&
          !isRegisterLoading &&
          [...failedFileTypes].some((key) => !key.includes(':')) && (
            <p className="text-[10px] text-muted-foreground mt-2">
              {t(
                'companyDetail.registerDocs.refreshFailedHint',
                'Something went wrong, please try again later.',
              )}
            </p>
          )}
        {showRegisterAttribution && (
          <SourceAttribution
            source="handelsregister"
            registerCourt={registerCourt}
            registerId={registerId}
            capturedAt={capturedAt}
          />
        )}
      </CardContent>

      <InsufficientCreditsDialog
        open={creditsDialogOpen}
        onOpenChange={setCreditsDialogOpen}
      />

      <CreditsUsedPopup
        snapshot={creditsUsed}
        onClose={() => setCreditsUsed(null)}
      />
    </Card>
  );
});
